import { useState, useEffect } from 'react';
import { FiPlus, FiTrash2, FiX, FiEye, FiFile, FiEdit3, FiClock, FiUser, FiUsers, FiActivity, FiCalendar } from 'react-icons/fi';
import api from '../services/api';
import ConfirmModal from '../components/ConfirmModal';
import { useAuth } from '../context/AuthContext';
import './Attachments.css';

const COLUMN_TYPES = [
  { value: 'text', icon: <FiEdit3 />, label: 'ტექსტი', color: '' },
  { value: 'activity', icon: <FiActivity />, label: 'აქტივობა', color: 'type-activity' },
  { value: 'dropdown', icon: '▾', label: 'დროპდაუნი', color: 'type-dropdown' },
  { value: 'datetime', icon: <FiClock />, label: 'თარიღი/დრო', color: 'type-date' },
  { value: 'worktime', icon: '🕐', label: 'დრო (დაწყ/დასრ)', color: 'type-worktime' },
  { value: 'duration', icon: '⏱', label: 'ხანგრძლივობა', color: 'type-duration' },
  { value: 'signature', icon: '✍', label: 'ხელმოწერა', color: 'type-sign' },
];

const HEADER_TYPES = [
  { value: 'text', icon: <FiEdit3 />, label: 'ტექსტი' },
  { value: 'client_dropdown', icon: <FiUsers />, label: 'ბენეფიციარი' },
  { value: 'auto_user', icon: <FiUser />, label: 'ავტო-იუზერი' },
];

const Attachments = () => {
  const { isSuperAdmin } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showPreview, setShowPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmAction, setConfirmAction] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const [title, setTitle] = useState('');
  const [mainTitle, setMainTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [headerFields, setHeaderFields] = useState([{ label: '', type: 'text' }]);
  const [columns, setColumns] = useState([{ name: '', type: 'text' }]);
  const [hasNote, setHasNote] = useState(true);
  const [showHeaderFields, setShowHeaderFields] = useState(true);
  const [allowedDays, setAllowedDays] = useState([0,1,2,3,4,5,6]);
  const [priority, setPriority] = useState(0);
  const [autoOpenMissed, setAutoOpenMissed] = useState(false);
  const [templateType, setTemplateType] = useState('standard');
  const [fillDeadline, setFillDeadline] = useState(null);

  const DAY_NAMES = ['ორშაბათი','სამშაბათი','ოთხშაბათი','ხუთშაბათი','პარასკევი','შაბათი','კვირა'];

  const fetchTemplates = async () => {
    try {
      const { data } = await api.get('/templates');
      setTemplates(data);
    } catch {
      // fetch failed silently
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTemplates(); }, []);

  const resetForm = () => {
    setTitle(''); setMainTitle(''); setSubtitle('');
    setHeaderFields([{ label: '', type: 'text' }]);
    setColumns([{ name: '', type: 'text' }]);
    setHasNote(true);
    setShowHeaderFields(true);
    setAllowedDays([0,1,2,3,4,5,6]);
    setPriority(0);
    setAutoOpenMissed(false);
    setTemplateType('standard');
    setFillDeadline(null);
    setEditingId(null);
  };

  const addHeaderField = () => setHeaderFields([...headerFields, { label: '', type: 'text' }]);
  const updateHeaderField = (idx, field, val) => {
    const u = [...headerFields]; u[idx] = { ...u[idx], [field]: val }; setHeaderFields(u);
  };
  const removeHeaderField = (idx) => {
    if (headerFields.length === 1) return;
    setHeaderFields(headerFields.filter((_, i) => i !== idx));
  };

  const addColumn = () => setColumns([...columns, { name: '', type: 'text' }]);
  const updateColumn = (idx, field, val) => {
    const u = [...columns]; u[idx] = { ...u[idx], [field]: val }; setColumns(u);
  };
  const removeColumn = (idx) => {
    if (columns.length === 1) return;
    setColumns(columns.filter((_, i) => i !== idx));
  };

  const openEdit = (t) => {
    setEditingId(t.id);
    setTitle(t.title);
    setMainTitle(t.main_title || '');
    setSubtitle(t.subtitle || '');
    setHeaderFields((t.header_fields || []).length ? t.header_fields : [{ label: '', type: 'text' }]);
    setColumns((t.columns || []).length ? t.columns : [{ name: '', type: 'text' }]);
    setHasNote(t.has_note !== false);
    setShowHeaderFields((t.header_fields || []).length > 0);
    setAllowedDays(t.allowed_days || [0,1,2,3,4,5,6]);
    setPriority(t.priority || 0);
    setAutoOpenMissed(t.auto_open_missed || false);
    setTemplateType(t.type || 'standard');
    setFillDeadline(t.fill_deadline != null ? t.fill_deadline : null);
    setShowCreate(true);
  };

  const handleSave = async () => {
    const payload = {
      title: title || `დანართი №${templates.length + 1}`,
      main_title: mainTitle,
      subtitle,
      header_fields: showHeaderFields ? headerFields.filter((f) => f.label.trim()) : [],
      columns: columns.filter((c) => c.name.trim()),
      has_note: hasNote,
      allowed_days: allowedDays,
      priority,
      auto_open_missed: autoOpenMissed,
      type: templateType,
      fill_deadline: fillDeadline,
    };
    try {
      if (editingId) {
        await api.put(`/templates/${editingId}`, payload);
      } else {
        await api.post('/templates', payload);
      }
      resetForm();
      setEditingId(null);
      setShowCreate(false);
      fetchTemplates();
    } catch {
      // create failed silently
    }
  };

  const toggleActive = async (id) => {
    try {
      await api.put(`/templates/${id}/toggle-active`);
      fetchTemplates();
    } catch { /* ignored */ }
  };

  const deleteTemplate = async (id) => {
    let count = 0;
    try {
      const { data } = await api.get(`/templates/${id}/filled-count`);
      count = data.count;
    } catch { /* ignored */ }

    const message = count > 0
      ? `ამ შაბლონზე ${count} შევსებული დანართია. წაშლისას ისინიც წაიშლება. ნამდვილად გსურთ?`
      : 'ნამდვილად გსურთ შაბლონის წაშლა?';

    setConfirmAction({
      title: 'შაბლონის წაშლა', message, type: 'delete',
      onConfirm: async () => { try { await api.delete(`/templates/${id}`); fetchTemplates(); } catch { /* ignored */ } setConfirmAction(null); },
    });
  };

  const getColTypeHint = (type) => {
    switch (type) { case 'signature': return ' ✍'; case 'datetime': return ' ⏱'; case 'worktime': return ' 🕐'; case 'duration': return ' ⏳'; case 'activity': return ' ▾'; case 'dropdown': return ' ▾'; default: return ''; }
  };
  const getColClass = (type) => {
    switch (type) { case 'signature': return 'paper-col-sign'; case 'datetime': return 'paper-col-datetime'; case 'worktime': return 'paper-col-worktime'; case 'duration': return 'paper-col-duration'; case 'activity': return 'paper-col-activity'; case 'dropdown': return 'paper-col-dropdown'; default: return ''; }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>დანართები</h1>
          <p className="page-subtitle">შაბლონების მართვა</p>
        </div>
        {isSuperAdmin && (
          <button className="btn-primary" onClick={() => { resetForm(); setShowCreate(true); }}>
            <FiPlus /> ახალი შაბლონი
          </button>
        )}
      </div>

      {loading ? <div className="loading">იტვირთება...</div> : (
        <div className="attachments-grid">
          {templates.map((t) => (
            <div key={t.id} className={`attachment-card ${t.is_active === false ? 'attachment-card-inactive' : ''}`}>
              <div className="attachment-icon"><FiFile /></div>
              <div className="attachment-info">
                <h3>{t.title}</h3>
                <span className="attachment-meta">{t.subtitle || 'შაბლონი'}</span>
                <div className="att-tags">
                  <span className="attachment-rows">{(t.columns || []).length} სვეტი</span>
                  {t.priority > 0 && <span className="att-priority">#{t.priority}</span>}
                  {t.type === 'calendar' && <span className="att-calendar">კალენდარი</span>}
                  {t.type === 'weekly' && <span className="att-weekly">კვირის გეგმა</span>}
                  {t.auto_open_missed && <span className="att-auto">ავტო</span>}
                  {t.is_active === false && <span className="att-inactive">არააქტიური</span>}
                </div>
              </div>
              <div className="attachment-actions">
                {isSuperAdmin && (
                  <button className={`btn-icon ${t.is_active === false ? 'btn-activate' : 'btn-active-on'}`} onClick={() => toggleActive(t.id)} title={t.is_active === false ? 'გააქტიურება' : 'დეაქტივაცია'}>
                    {t.is_active === false ? '○' : '●'}
                  </button>
                )}
                <button className="btn-icon btn-edit" onClick={() => setShowPreview(t)}><FiEye /></button>
                {isSuperAdmin && <button className="btn-icon btn-edit" onClick={() => openEdit(t)} title="რედაქტირება"><FiEdit3 /></button>}
                {isSuperAdmin && <button className="btn-icon btn-danger" onClick={() => deleteTemplate(t.id)}><FiTrash2 /></button>}
              </div>
            </div>
          ))}
          {templates.length === 0 && <div className="empty-state-card">შაბლონები არ არის</div>}
        </div>
      )}

      {/* Create */}
      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="modal attachment-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingId ? 'შაბლონის რედაქტირება' : 'ახალი შაბლონი'}</h3>
              <button className="btn-icon" onClick={() => { setShowCreate(false); setEditingId(null); }}><FiX /></button>
            </div>
            <div className="template-builder">
              {/* Type selector */}
              <div className="builder-section">
                <div className="builder-section-title">ტიპი</div>
                <div className="type-toggle">
                  <button type="button" className={`type-toggle-btn ${templateType === 'standard' ? 'active' : ''}`} onClick={() => setTemplateType('standard')}>
                    <FiFile /> სტანდარტული
                  </button>
                  <button type="button" className={`type-toggle-btn type-weekly ${templateType === 'weekly' ? 'active' : ''}`} onClick={() => setTemplateType('weekly')}>
                    <FiCalendar /> კვირის გეგმა
                  </button>
                </div>
              </div>

              <div className="builder-section">
                <div className="builder-section-title">დანართის ტიპი</div>
                <div className="form-group">
                  <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="მაგ: დანართი №6 (ზედა მარჯვენა კუთხეში)" /></div>
              </div>

              <div className="builder-section">
                <div className="builder-section-title">სათაური და ქვესათაური</div>
                <div className="form-group"><label>სათაური</label>
                  <input value={mainTitle} onChange={(e) => setMainTitle(e.target.value)} placeholder="მაგ: ინდივიდუალური გეგმა (ცენტრში, მუქად)" /></div>
                <div className="form-group"><label>ქვესათაური</label>
                  <input value={subtitle} onChange={(e) => setSubtitle(e.target.value)} placeholder="მაგ: მომსახურების გაწევის აღრიცხვის ფორმა (ცენტრში, ღიად)" /></div>
              </div>

              <div className="builder-section">
                <div className="builder-section-title" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  სათაურის ველები
                  <label className="checkbox-label" style={{margin: 0, fontSize: 12}}><input type="checkbox" checked={showHeaderFields} onChange={(e) => setShowHeaderFields(e.target.checked)} /><span>{showHeaderFields ? 'ჩართული' : 'გამორთული'}</span></label>
                </div>
                {showHeaderFields && <>
                <p className="builder-hint">ტიპი: ტექსტი, ბენეფიციარის dropdown ან ავტო-იუზერი</p>
                {headerFields.map((f, idx) => (
                  <div key={idx} className="builder-header-row">
                    <div className="header-top">
                      <input className="col-name-input" value={f.label} onChange={(e) => updateHeaderField(idx, 'label', e.target.value)} placeholder="ველის სახელი" />
                      <button type="button" className="btn-icon btn-danger btn-sm" onClick={() => removeHeaderField(idx)} disabled={headerFields.length === 1}><FiTrash2 /></button>
                    </div>
                    <div className="header-type-section">
                      {HEADER_TYPES.map((ht) => (
                        <button key={ht.value} type="button"
                          className={`header-type-btn ${ht.value === 'client_dropdown' ? 'type-client' : ht.value === 'auto_user' ? 'type-user' : ''} ${f.type === ht.value ? 'active' : ''}`}
                          onClick={() => updateHeaderField(idx, 'type', ht.value)}>
                          <span className="ht-icon">{ht.icon}</span>{ht.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
                <button type="button" className="add-row-btn" onClick={addHeaderField}><FiPlus /> ველის დამატება</button>
                </>}
              </div>

              <div className="builder-section">
                <div className="builder-section-title">ცხრილის სვეტები</div>
                {columns.map((c, idx) => (
                  <div key={idx} className="builder-column-row">
                    <div className="col-top">
                      <input className="col-name-input" value={c.name} onChange={(e) => updateColumn(idx, 'name', e.target.value)} placeholder="სვეტის სახელი" />
                      <button type="button" className="btn-icon btn-danger btn-sm" onClick={() => removeColumn(idx)} disabled={columns.length === 1}><FiTrash2 /></button>
                    </div>
                    <div className="col-type-section">
                      {COLUMN_TYPES.map((ct) => (
                        <button key={ct.value} type="button"
                          className={`col-type-btn ${ct.color} ${c.type === ct.value ? 'active' : ''}`}
                          onClick={() => updateColumn(idx, 'type', ct.value)}>
                          <span className="ct-icon">{ct.icon}</span>{ct.label}
                        </button>
                      ))}
                    </div>
                    {c.type === 'dropdown' && (
                      <div className="col-dropdown-options">
                        <label className="col-dropdown-label">დროპდაუნის ვარიანტები (თითო ხაზზე)</label>
                        <textarea
                          className="col-dropdown-textarea"
                          value={(c.options || []).join('\n')}
                          onChange={(e) => updateColumn(idx, 'options', e.target.value.split('\n'))}
                          placeholder={"ვარიანტი 1\nვარიანტი 2\nვარიანტი 3"}
                          rows={4}
                        />
                      </div>
                    )}
                    <div className="col-options-row">
                      <label className="fullrow-toggle">
                        <input type="checkbox" checked={c.fullRow || false} onChange={(e) => updateColumn(idx, 'fullRow', e.target.checked)} />
                        <span className="fullrow-label">მთლიანი row</span>
                      </label>
                      <label className="fullrow-toggle">
                        <input type="checkbox" checked={c.required || false} onChange={(e) => updateColumn(idx, 'required', e.target.checked)} />
                        <span className="fullrow-label" style={{color: c.required ? '#e53e3e' : undefined}}>აუცილებელი</span>
                      </label>
                      <div className="col-width-wrap">
                        <span className="col-width-label">სიგანე:</span>
                        <input type="number" className="col-width-input" min="5" max="100" value={c.width || ''} onChange={(e) => updateColumn(idx, 'width', e.target.value ? parseInt(e.target.value) : null)} placeholder="ავტო" />
                        <span className="col-width-unit">%</span>
                      </div>
                    </div>
                  </div>
                ))}
                <button type="button" className="add-row-btn" onClick={addColumn}><FiPlus /> სვეტის დამატება</button>
              </div>

              <div className="builder-section">
                <div className="builder-section-title">განრიგი და პრიორიტეტი</div>
                <p className="builder-hint">მიუთითეთ რომელ დღეებში შეივსება და რა თანმიმდევრობით</p>

                <div className="days-grid">
                  {DAY_NAMES.map((name, i) => (
                    <button key={i} type="button"
                      className={`day-btn ${allowedDays.includes(i) ? 'active' : ''}`}
                      onClick={() => setAllowedDays(prev => prev.includes(i) ? prev.filter(d => d !== i) : [...prev, i].sort())}
                    >
                      {name.slice(0, 3)}
                    </button>
                  ))}
                </div>

                <div className="form-group" style={{marginTop: 14}}>
                  <label>პრიორიტეტი (რიგითობა)</label>
                  <div className="priority-input-wrap">
                    <input type="number" min="0" value={priority} onChange={(e) => setPriority(parseInt(e.target.value) || 0)} />
                    <span className="priority-hint">დაბალი რიცხვი = პირველი შეივსება</span>
                  </div>
                </div>

                <label className="checkbox-label"><input type="checkbox" checked={autoOpenMissed} onChange={(e) => setAutoOpenMissed(e.target.checked)} /><span>გამოტოვებული დღის ავტო-გახსნა</span></label>
                <label className="checkbox-label"><input type="checkbox" checked={hasNote} onChange={(e) => setHasNote(e.target.checked)} /><span>შენიშვნის ველი</span></label>

                <div className="form-group" style={{marginTop: 14}}>
                  <label>შევსების ვადა (დღე)</label>
                  <p className="builder-hint">თუ ამ დღემდე არ შეივსება, ავტომატურად გაეხსნება</p>
                  <select value={fillDeadline != null ? fillDeadline : ''} onChange={(e) => setFillDeadline(e.target.value === '' ? null : parseInt(e.target.value))}
                    style={{width: '100%', padding: '10px 14px', border: '2px solid #e2e8f0', borderRadius: 10, fontSize: 14, background: '#f8fafc', fontFamily: 'Inter, sans-serif', outline: 'none'}}>
                    <option value="">არ არის</option>
                    {DAY_NAMES.map((name, i) => (
                      <option key={i} value={i}>{name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <button type="button" className="btn-primary btn-full" onClick={handleSave}>{editingId ? 'შენახვა' : 'შაბლონის შექმნა'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Preview */}
      {showPreview && (
        <div className="modal-overlay" onClick={() => setShowPreview(null)}>
          <div className="modal attachment-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>შაბლონის ნახვა</h3>
              <button className="btn-icon" onClick={() => setShowPreview(null)}><FiX /></button>
            </div>
            <div className="paper">
              <div className="paper-title-right">{showPreview.title}</div>
              {showPreview.main_title && (
                <div className="paper-center"><div className="paper-main-title">{showPreview.main_title}</div></div>
              )}
              {showPreview.subtitle && (
                <div className="paper-center"><div className="paper-subtitle">{showPreview.subtitle}</div></div>
              )}
              <div className="paper-fields">
                {(showPreview.header_fields || []).map((f, idx) => (
                  <div key={idx} className="paper-field">
                    <span className="paper-field-label">{f.label}</span>
                    {f.type === 'client_dropdown' && <span className="paper-field-badge badge-client">dropdown</span>}
                    {f.type === 'auto_user' && <span className="paper-field-badge badge-user">ავტო</span>}
                    <span className="paper-field-line"></span>
                  </div>
                ))}
              </div>
              <div className="paper-table-wrap">
                {(() => {
                  const cols = showPreview.columns || [];
                  if (showPreview.type === 'weekly') {
                    const days = (showPreview.allowed_days || [0,1,2,3,4,5,6]).sort((a, b) => a - b);
                    const visibleCols = cols.filter(c => c.type !== 'datetime' && c.type !== 'worktime' && c.type !== 'duration');
                    return (
                      <table className="paper-table">
                        <thead><tr>
                          {visibleCols.map((c, i) => (
                            <th key={i} className={`${getColClass(c.type)} ${c.fullRow ? 'paper-col-fullrow' : ''}`}>{c.name}<span className="paper-sign-hint">{getColTypeHint(c.type)}</span></th>
                          ))}
                        </tr></thead>
                        <tbody>
                          {days.map((d) => (
                            <tr key={d}>
                              {visibleCols.map((c, ci) => {
                                if (c.fullRow && d === days[0]) {
                                  return <td key={ci} rowSpan={days.length} className={`${getColClass(c.type)} paper-cell-fullrow`}></td>;
                                }
                                if (c.fullRow) return null;
                                return <td key={ci} className={getColClass(c.type)}></td>;
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    );
                  }
                  const rowCount = 8;
                  return (
                    <table className="paper-table">
                      <thead><tr><th className="paper-col-num">№</th>
                        {cols.map((c, i) => (
                          <th key={i} className={`${getColClass(c.type)} ${c.fullRow ? 'paper-col-fullrow' : ''}`} style={c.width ? {width: `${c.width}%`} : undefined}>{c.name}<span className="paper-sign-hint">{getColTypeHint(c.type)}</span></th>
                        ))}</tr></thead>
                      <tbody>
                        {[...Array(rowCount)].map((_, ri) => (
                          <tr key={ri}>
                            <td className="paper-col-num"></td>
                            {cols.map((c, ci) => {
                              if (c.fullRow && ri === 0) {
                                return <td key={ci} rowSpan={rowCount} className={`${getColClass(c.type)} paper-cell-fullrow`}></td>;
                              }
                              if (c.fullRow) return null;
                              return <td key={ci} className={getColClass(c.type)}></td>;
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  );
                })()}
              </div>
              {showPreview.has_note && (
                <div className="paper-note"><span className="paper-note-label">შენიშვნა:</span>
                  <div className="paper-note-lines"><div className="paper-note-line"></div><div className="paper-note-line"></div><div className="paper-note-line"></div></div></div>
              )}
            </div>
          </div>
        </div>
      )}
      {confirmAction && <ConfirmModal {...confirmAction} onCancel={() => setConfirmAction(null)} />}
    </div>
  );
};

export default Attachments;
